import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/lib/useSeo";
import {
  AuthShell,
  PrimaryButton,
  TextField,
  ErrorBox,
  OtpBoxes,
  EyeToggle,
} from "@/components/AuthKit";

/**
 * ResetPassword — 1:1 port of iOS `src/app/(auth)/reset.tsx`.
 * Single-screen: (email if not prefilled) + 6-digit code + new password
 * + confirm password → confirmForgotPassword → back to login.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const emailFromUrl = sp.get("email") || "";
  const { confirmForgotPassword, apiError } = useAuth();

  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [cpw, setCpw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setErr("");
    const otp = code.replace(/\D/g, "");
    if (otp.length !== 6) return setErr("Enter the 6-digit code from your email.");
    if (!email.trim()) return setErr("Email is required.");
    if (pw !== cpw) return setErr("Passwords do not match.");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");

    setLoading(true);
    try {
      await confirmForgotPassword({ email: email.trim(), otp, new_password: pw });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="reset-screen"
      title="Set a new password"
      subtitle={
        email
          ? `Enter the code we sent to ${email} and choose a new password.`
          : "Enter the code we sent to your email and choose a new password."
      }
    >
      <Seo
        title="Reset password — BrontoY"
        description="Enter the reset code sent to your email and choose a new password for your BrontoY account."
        canonicalPath="/reset"
        noindex
      />
      <form onSubmit={submit} className="flex flex-col gap-5 mt-8">
        {!emailFromUrl ? (
          <TextField
            id="reset-email"
            label="EMAIL"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="reset-email-input"
          />
        ) : null}

        <div className="flex flex-col gap-2.5">
          <label
            className="text-muted-foreground font-semibold"
            style={{ fontSize: 13, letterSpacing: "0.02em" }}
          >
            RESET CODE
          </label>
          <OtpBoxes length={6} value={code} onChange={setCode} testId="reset-otp" />
        </div>

        <TextField
          id="reset-password"
          label="NEW PASSWORD"
          type={showPw ? "text" : "password"}
          autoCapitalize="none"
          placeholder="At least 8 characters"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          rightAdornment={
            <EyeToggle show={showPw} onToggle={() => setShowPw((s) => !s)} testId="reset-toggle-pw" />
          }
          testId="reset-password-input"
        />

        <TextField
          id="reset-confirm"
          label="CONFIRM PASSWORD"
          type={showPw ? "text" : "password"}
          autoCapitalize="none"
          placeholder="Re-enter password"
          value={cpw}
          onChange={(e) => setCpw(e.target.value)}
          testId="reset-confirm-input"
        />

        {err ? <ErrorBox testId="reset-error">{err}</ErrorBox> : null}

        <PrimaryButton
          type="submit"
          label="Update password"
          loading={loading}
          className="mt-1"
          testId="reset-submit-btn"
        />
      </form>

      <div className="flex items-center justify-center mt-7 text-[14px]">
        <span className="text-muted-foreground">Didn&apos;t get a code?&nbsp;</span>
        <button
          type="button"
          onClick={() => navigate("/forgot", { replace: true })}
          className="text-foreground font-bold hover:opacity-80"
          data-testid="reset-back-to-forgot"
        >
          Send another
        </button>
      </div>
    </AuthShell>
  );
}
